import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Root, classes } from './utils';
import {
  getCheckboxField,
  postCheckboxFIeldStatus,
  selectAllCheckboxStatus,
} from '../../../../services/accountSetting';
import { SETTING } from '../../../../constants/staticContent';
import ROLES from '../../../../constants/roles';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import Toast from '../../utils/Toast';
import { getAdminActivityHistory } from '../../../../services/userApi';

const DataFields = ({ documentType }) => {
  let beforeSupplierCount;
  let beforePaymentMethodCount;
  let beforePaymentCount;
  let beforeCustomerCount;
  let beforeProductCount;
  let beforeBankDetailsCount;
  switch (documentType) {
    case 'bs_field':
      beforeSupplierCount = 10;
      beforePaymentMethodCount = 16;
      beforePaymentCount = 24;
      beforeCustomerCount = 28;
      break;
    case 'cc_field':
      beforeSupplierCount = 4;
      beforePaymentMethodCount = 9;
      beforePaymentCount = 13;
      beforeCustomerCount = 21;
      break;
    default:
      beforeSupplierCount = 5;
      beforePaymentMethodCount = 14;
      beforePaymentCount = 17;
      beforeCustomerCount = 27;
      beforeProductCount = 36;
      beforeBankDetailsCount = 49;
      break;
  }
  const role = getLocalStorageItem('role');
  const userDetail = useSelector((state) => state.user.user);

  const [selectAll, setSelectAll] = useState(false);
  const [allCheckFieldStatus, setAllCheckFieldStatus] = useState(
    Array(41).fill(true),
  );
  const [invoiceReceiptData, setInvoiceReceiptData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [invoiceCheckboxStatus, setInvoiceCheckboxStatus] = useState([]);
  const [customerCheckboxStatus, setCustomerCheckboxStatus] = useState([]);
  const [paymentCheckboxStatus, setPaymentCheckboxStatus] = useState([]);
  const [supplierCheckboxStatus, setSupplierCheckboxStatus] = useState([]);
  const [productCheckboxStatus, setProductCheckboxStatus] = useState([]);
  const [checkFieldDisable, setCheckFieldDisable] = useState(false);
  const [paymentMethodCheckboxStatus, setPaymentMethodCheckboxStatus] =
    useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bankDetails, setBankDetails] = useState([]);
  const [bankDetailsCheckboxStatus, setBankDetailsCheckBoxStatus] =
    useState(false);

  const labelMapping = {
    invoice_field: {
      label1: 'Invoice',
      label2: 'Supplier',
      label3: 'Payment Method',
      label4: 'Payment',
      label5: 'Customer',
      label6: 'Product',
      label7: 'Bank Details',
    },
    sales_field: {
      label1: 'Sales',
      label2: 'Supplier',
      label3: 'Payment Method',
      label4: 'Payment',
      label5: 'Customer',
      label6: 'Product',
      label7: 'Bank Details',
    },
    receipt_field: {
      label1: 'Receipt',
      label2: 'Supplier',
      label3: 'Payment Method',
      label4: 'Payment',
      label5: 'Customer',
      label6: 'Product',
      label7: 'Bank Details',
    },
    cc_field: {
      label1: 'Card Holder',
      label2: 'Card Info',
      label3: 'GST Info',
      label4: 'Statement',
      label5: 'Transactions',
    },
    bs_field: {
      label1: 'Account Holder Details',
      label2: 'Statement Details',
      label3: 'Transactions',
      label4: 'Summary',
      label5: 'Bank Charges',
    },
  };

  const getLabel = (item, labelType) => {
    const itemLabels = labelMapping[item] || labelMapping.cc_field;
    return itemLabels[labelType] || null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (role !== ROLES.MEMBER) {
          setCheckFieldDisable(false);
        } else {
          setCheckFieldDisable(true);
        }

        const data = await getCheckboxField(documentType);
        if (data) {
          setIsLoading(false);
        }
        if (data && Object.keys(userDetail).length !== 0) {
          let documentItem;
          switch (documentType) {
            case 'invoice_field':
            case 'sales_field':
              documentItem = data.Invoice;
              break;
            case 'receipt_field':
              documentItem = data.Receipt;
              break;
            case 'cc_field':
              documentItem = data.Card_Holder;
              break;
            case 'bs_field':
              documentItem = data.Account_Holder_Details;
              break;
            default:
              documentItem = null;
              break;
          }
          setInvoiceReceiptData(documentItem);
          let CustomerStatus;
          let PaymentStatus;
          let SupplierStatus;
          let PaymentMethodStatus;
          let ProductStatus;
          switch (documentType) {
            case 'bs_field':
              setCustomerData(data.Bank_Charges);
              setPaymentData(data.Summary);
              setSupplierData(data.Statement_Details);
              setPaymentMethodData(data.Transactions);
              CustomerStatus = data.Bank_Charges.map((obj) => obj?.status);
              PaymentStatus = data.Summary.map((obj) => obj?.status);
              SupplierStatus = data.Statement_Details.map((obj) => obj?.status);
              PaymentMethodStatus = data.Transactions.map((obj) => obj?.status);
              break;
            case 'cc_field':
              setCustomerData(data.Transactions);
              setPaymentData(data.Statement);
              setSupplierData(data.Card_Info);
              setPaymentMethodData(data.Gst_Info);
              CustomerStatus = data.Transactions.map((obj) => obj?.status);
              PaymentStatus = data.Statement.map((obj) => obj?.status);
              SupplierStatus = data.Card_Info.map((obj) => obj?.status);
              PaymentMethodStatus = data.Gst_Info.map((obj) => obj?.status);
              break;
            default:
              setCustomerData(data.Customer);
              setPaymentData(data.Payment);
              setSupplierData(data.Supplier);
              setPaymentMethodData(data.Payment_Method);
              CustomerStatus = data.Customer.map((obj) => obj?.status);
              PaymentStatus = data.Payment.map((obj) => obj?.status);
              SupplierStatus = data.Supplier.map((obj) => obj?.status);
              PaymentMethodStatus = data.Payment_Method.map(
                (obj) => obj?.status,
              );
              break;
          }
          let bankDetailsStatus;
          let bankDetailsAllTrue;
          if (
            documentType !== 'receipt_field' &&
            documentType !== 'cc_field' &&
            documentType !== 'bs_field'
          ) {
            setBankDetails(data?.bank_details);
            bankDetailsStatus = data.bank_details.map((obj) => obj?.status);
            bankDetailsAllTrue = bankDetailsStatus.every(
              (value) => value === true,
            );
          }
          let productAllTrue;
          if (documentType !== 'cc_field' && documentType !== 'bs_field') {
            setProductData(data.Product);
            ProductStatus = data.Product.map((obj) => obj?.status);
            productAllTrue = ProductStatus.every((value) => value === true);
          }
          const InvoiceStatus =
            documentItem && documentItem.map((obj) => obj?.status);

          const invoiceAllTrue = InvoiceStatus.every((value) => value === true);
          const supplierAllTrue = CustomerStatus.every(
            (value) => value === true,
          );
          const paymentMethodAllTrue = PaymentStatus.every(
            (value) => value === true,
          );
          const paymentAllTrue = SupplierStatus.every(
            (value) => value === true,
          );
          const customerAllTrue = PaymentMethodStatus.every(
            (value) => value === true,
          );

          if (
            invoiceAllTrue &&
            supplierAllTrue &&
            paymentMethodAllTrue &&
            paymentAllTrue &&
            customerAllTrue &&
            (documentType !== 'cc_field' && documentType !== 'bs_field'
              ? productAllTrue
              : true) &&
            (documentType !== 'receipt_field' &&
            documentType !== 'bs_field' &&
            documentType !== 'cc_field'
              ? bankDetailsAllTrue
              : true)
          ) {
            setSelectAll(true);
          } else {
            setSelectAll(false);
          }
          if (documentType === 'receipt_field') {
            setAllCheckFieldStatus([
              ...InvoiceStatus,
              ...SupplierStatus,
              ...PaymentMethodStatus,
              ...PaymentStatus,
              ...CustomerStatus,
              ...ProductStatus,
            ]);
            setInvoiceCheckboxStatus(InvoiceStatus);
            setCustomerCheckboxStatus(CustomerStatus);
            setPaymentCheckboxStatus(PaymentStatus);
            setSupplierCheckboxStatus(SupplierStatus);
            setPaymentMethodCheckboxStatus(PaymentMethodStatus);
            setProductCheckboxStatus(ProductStatus);
          } else if (documentType === 'bs_field') {
            setAllCheckFieldStatus([
              ...InvoiceStatus,
              ...SupplierStatus,
              ...PaymentMethodStatus,
              ...PaymentStatus,
              ...CustomerStatus,
            ]);
            setInvoiceCheckboxStatus(InvoiceStatus);
            setCustomerCheckboxStatus(CustomerStatus);
            setPaymentCheckboxStatus(PaymentStatus);
            setSupplierCheckboxStatus(SupplierStatus);
            setPaymentMethodCheckboxStatus(PaymentMethodStatus);
          } else if (documentType === 'cc_field') {
            setAllCheckFieldStatus([
              ...InvoiceStatus,
              ...SupplierStatus,
              ...PaymentMethodStatus,
              ...PaymentStatus,
              ...CustomerStatus,
            ]);
            setInvoiceCheckboxStatus(InvoiceStatus);
            setCustomerCheckboxStatus(CustomerStatus);
            setPaymentCheckboxStatus(PaymentStatus);
            setSupplierCheckboxStatus(SupplierStatus);
            setPaymentMethodCheckboxStatus(PaymentMethodStatus);
          } else {
            setAllCheckFieldStatus([
              ...InvoiceStatus,
              ...SupplierStatus,
              ...PaymentMethodStatus,
              ...PaymentStatus,
              ...CustomerStatus,
              ...ProductStatus,
              ...bankDetailsStatus,
            ]);
            setInvoiceCheckboxStatus(InvoiceStatus);
            setCustomerCheckboxStatus(CustomerStatus);
            setPaymentCheckboxStatus(PaymentStatus);
            setSupplierCheckboxStatus(SupplierStatus);
            setPaymentMethodCheckboxStatus(PaymentMethodStatus);
            setProductCheckboxStatus(ProductStatus);
            setBankDetailsCheckBoxStatus(bankDetailsStatus);
          }
        }
      } catch (error) {
        setIsLoading(false);
        return error;
      }
    };

    if (userDetail && Object.keys(userDetail).length !== 0) {
      fetchData();
    }
  }, [role, userDetail]);

  const getActivityHistory = async () => {
    try {
      if (role === ROLES.ADMIN) {
        const response = await getAdminActivityHistory();
        if (response.length === 1) {
          if (documentType === 'cc_field') {
            setAllCheckFieldStatus(Array(25).fill(true));
          } else if (documentType === 'bs_field') {
            setAllCheckFieldStatus(Array(32).fill(true));
          } else {
            setAllCheckFieldStatus(Array(51).fill(true));
          }
          await selectAllCheckboxStatus(true, documentType);
        }
      }
    } catch (err) {
      return err;
    }
  };

  const setTrueFieldsDefault = async () => {
    await getActivityHistory();
  };

  useEffect(() => {
    if (role === ROLES.ADMIN) {
      setTrueFieldsDefault();
    }
  }, []);

  const handleSelectAllChange = async (event) => {
    const { checked } = event.target;
    const res = await selectAllCheckboxStatus(checked, documentType);
    if ((role === ROLES.ADMIN || role === ROLES.MANAGER) && res?.message) {
      setSelectAll(checked);
      switch (documentType) {
        case 'bs_field':
          if (checked) {
            setAllCheckFieldStatus(Array(33).fill(true));
            setInvoiceCheckboxStatus(Array(10).fill(true));
            setSupplierCheckboxStatus(Array(6).fill(true));
            setPaymentMethodCheckboxStatus(Array(8).fill(true));
            setPaymentCheckboxStatus(Array(5).fill(true));
            setCustomerCheckboxStatus(Array(4).fill(true));
          } else {
            setAllCheckFieldStatus(Array(32).fill(false));
            setInvoiceCheckboxStatus([
              false,
              false,
              false,
              false,
              true,
              false,
              false,
              false,
              false,
              false,
            ]);
            setSupplierCheckboxStatus(Array(6).fill(false));
            setPaymentMethodCheckboxStatus(Array(8).fill(false));
            setPaymentCheckboxStatus(Array(5).fill(false));
            setCustomerCheckboxStatus(Array(4).fill(false));
          }
          break;
        case 'cc_field':
          if (checked) {
            setAllCheckFieldStatus(Array(26).fill(true));
            setInvoiceCheckboxStatus(Array(4).fill(true));
            setSupplierCheckboxStatus(Array(5).fill(true));
            setPaymentMethodCheckboxStatus(Array(4).fill(true));
            setPaymentCheckboxStatus(Array(8).fill(true));
            setCustomerCheckboxStatus(Array(5).fill(true));
          } else {
            setAllCheckFieldStatus(Array(25).fill(false));
            setInvoiceCheckboxStatus(Array(4).fill(false));
            setSupplierCheckboxStatus([true, false, false, false, false]);
            setPaymentMethodCheckboxStatus(Array(4).fill(false));
            setPaymentCheckboxStatus(Array(8).fill(false));
            setCustomerCheckboxStatus(Array(5).fill(false));
          }
          break;
        default:
          if (checked) {
            setAllCheckFieldStatus(Array(53).fill(true));
            setInvoiceCheckboxStatus(Array(5).fill(true));
            setSupplierCheckboxStatus(Array(9).fill(true));
            setPaymentMethodCheckboxStatus(Array(3).fill(true));
            setPaymentCheckboxStatus(Array(10).fill(true));
            setCustomerCheckboxStatus(Array(9).fill(true));
            setProductCheckboxStatus(Array(13).fill(true));
            setBankDetailsCheckBoxStatus(Array(4).fill(true));
          } else {
            setAllCheckFieldStatus(Array(52).fill(false));
            if (documentType === 'receipt_field') {
              setInvoiceCheckboxStatus([false, true]);
            } else {
              setInvoiceCheckboxStatus([false, false, false, true, false]);
            }
            setSupplierCheckboxStatus(Array(9).fill(false));
            setPaymentMethodCheckboxStatus(Array(3).fill(false));
            setPaymentCheckboxStatus(Array(10).fill(false));
            setCustomerCheckboxStatus(Array(9).fill(false));
            setProductCheckboxStatus(Array(13).fill(false));
            setBankDetailsCheckBoxStatus(Array(4).fill(false));
          }
          break;
      }
    }
  };

  const handleCheckboxChange = (index, id) => async (event) => {
    const checkboxStatus = event.target.checked;
    allCheckFieldStatus[index] = checkboxStatus;
    const invoiceAllFalse = allCheckFieldStatus.every(
      (value) => value === false,
    );
    if (!invoiceAllFalse) {
      const res = await postCheckboxFIeldStatus(id, checkboxStatus);
      if ((role === ROLES.ADMIN || role === ROLES.MANAGER) && res?.message) {
        let newModifiedArray;
        switch (documentType) {
          case 'bs_field':
            if (index < 10) {
              newModifiedArray = [...invoiceCheckboxStatus];
              newModifiedArray[index] = checkboxStatus;
              setInvoiceCheckboxStatus(newModifiedArray);
            } else if (index > 9 && index < 16) {
              newModifiedArray = [...supplierCheckboxStatus];
              newModifiedArray[index - beforeSupplierCount] = checkboxStatus;
              setSupplierCheckboxStatus(newModifiedArray);
            } else if (index > 15 && index < 24) {
              newModifiedArray = [...paymentMethodCheckboxStatus];
              newModifiedArray[index - beforePaymentMethodCount] =
                checkboxStatus;
              setPaymentMethodCheckboxStatus(newModifiedArray);
            } else if (index > 23 && index < 29) {
              newModifiedArray = [...paymentCheckboxStatus];
              newModifiedArray[index - beforePaymentCount] = checkboxStatus;
              setPaymentCheckboxStatus(newModifiedArray);
            } else if (index > 28 && index < 33) {
              newModifiedArray = [...customerCheckboxStatus];
              newModifiedArray[index - beforeCustomerCount] = checkboxStatus;
              setCustomerCheckboxStatus(newModifiedArray);
            }
            break;
          case 'cc_field':
            if (index < 4) {
              newModifiedArray = [...invoiceCheckboxStatus];
              newModifiedArray[index] = checkboxStatus;
              setInvoiceCheckboxStatus(newModifiedArray);
            } else if (index > 3 && index < 9) {
              newModifiedArray = [...supplierCheckboxStatus];
              newModifiedArray[index - beforeSupplierCount] = checkboxStatus;
              setSupplierCheckboxStatus(newModifiedArray);
            } else if (index > 8 && index < 13) {
              newModifiedArray = [...paymentMethodCheckboxStatus];
              newModifiedArray[index - beforePaymentMethodCount] =
                checkboxStatus;
              setPaymentMethodCheckboxStatus(newModifiedArray);
            } else if (index > 12 && index < 21) {
              newModifiedArray = [...paymentCheckboxStatus];
              newModifiedArray[index - beforePaymentCount] = checkboxStatus;
              setPaymentCheckboxStatus(newModifiedArray);
            } else if (index > 20 && index < 26) {
              newModifiedArray = [...customerCheckboxStatus];
              newModifiedArray[index - beforeCustomerCount] = checkboxStatus;
              setCustomerCheckboxStatus(newModifiedArray);
            }
            break;
          default:
            if (index < 5) {
              newModifiedArray = [...invoiceCheckboxStatus];
              newModifiedArray[index] = checkboxStatus;
              setInvoiceCheckboxStatus(newModifiedArray);
            } else if (index > 4 && index < 14) {
              newModifiedArray = [...supplierCheckboxStatus];
              newModifiedArray[index - beforeSupplierCount] = checkboxStatus;
              setSupplierCheckboxStatus(newModifiedArray);
            } else if (index > 13 && index < 17) {
              newModifiedArray = [...paymentMethodCheckboxStatus];
              newModifiedArray[index - beforePaymentMethodCount] =
                checkboxStatus;
              setPaymentMethodCheckboxStatus(newModifiedArray);
            } else if (index > 16 && index < 27) {
              newModifiedArray = [...paymentCheckboxStatus];
              newModifiedArray[index - beforePaymentCount] = checkboxStatus;
              setPaymentCheckboxStatus(newModifiedArray);
            } else if (index > 26 && index < 36) {
              newModifiedArray = [...customerCheckboxStatus];
              newModifiedArray[index - beforeCustomerCount] = checkboxStatus;
              setCustomerCheckboxStatus(newModifiedArray);
            } else if (index > 35 && index < 49) {
              newModifiedArray = [...productCheckboxStatus];
              newModifiedArray[index - beforeProductCount] = checkboxStatus;
              setProductCheckboxStatus(newModifiedArray);
            } else if (index > 48 && index < 53) {
              newModifiedArray = [...bankDetailsCheckboxStatus];
              newModifiedArray[index - beforeBankDetailsCount] = checkboxStatus;
              setBankDetailsCheckBoxStatus(newModifiedArray);
            }
            break;
        }
        const invoiceAllTrue = allCheckFieldStatus.every(
          (value) => value === true,
        );
        if (invoiceAllTrue) {
          setSelectAll(true);
        } else {
          setSelectAll(false);
        }
      }
    } else if (invoiceAllFalse === true) {
      allCheckFieldStatus[index] = !checkboxStatus;
      Toast({ message: SETTING.DATA_FIELD_VALIDATION, variant: 'warning' });
    }
  };

  return (
    <Root>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container className={classes.dataFieldContainer}>
          <Grid item>
            <Grid container>
              <Grid item xs={4} sm={4} md={4} lg={4} xl={4} xxl={4}>
                <Box className={classes.selectAllContainer}>
                  <FormControlLabel
                    className={classes.checkBoxMainClass}
                    control={
                      <Checkbox
                        disabled={checkFieldDisable}
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                      />
                    }
                    label={
                      <Typography
                        variant="subtitle1"
                        className={classes.checkBoxPaddingLeft}
                      >
                        Select All
                      </Typography>
                    }
                  />
                </Box>
                <Typography
                  variant="body2"
                  className={classes.invoiceDataFieldLabel}
                >
                  {getLabel(documentType, 'label1')}
                </Typography>
                {invoiceReceiptData?.map((ele, i) => (
                  <FormControlLabel
                    key={`Invoice${i + 1}`}
                    className={classes.checkBoxMainClass}
                    control={
                      <Checkbox
                        disabled={checkFieldDisable}
                        checked={invoiceCheckboxStatus[i]}
                        onChange={handleCheckboxChange(i, ele.id)}
                      />
                    }
                    label={
                      <Typography
                        variant="subtitle1"
                        className={classes.checkBoxPaddingLeft}
                      >
                        {ele.short_desc}
                      </Typography>
                    }
                  />
                ))}
                <Typography variant="body2" className={classes.dataFieldLabel}>
                  {getLabel(documentType, 'label2')}
                </Typography>
                <Box className={classes.dataFieldsContainer}>
                  {supplierData?.map((ele, i) => (
                    <FormControlLabel
                      key={`Supplier${i + 1}`}
                      className={classes.checkBoxMainClass}
                      control={
                        <Checkbox
                          disabled={checkFieldDisable}
                          checked={supplierCheckboxStatus[i]}
                          onChange={handleCheckboxChange(
                            i + beforeSupplierCount,
                            ele.id,
                          )}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle1"
                          className={classes.checkBoxPaddingLeft}
                        >
                          {ele.short_desc}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
                <Typography variant="body2" className={classes.dataFieldLabel}>
                  {getLabel(documentType, 'label3')}
                </Typography>
                <Box className={classes.dataFieldsContainer}>
                  {paymentMethodData?.map((ele, i) => (
                    <FormControlLabel
                      key={`paymentMethod${i + 1}`}
                      className={classes.checkBoxMainClass}
                      control={
                        <Checkbox
                          disabled={checkFieldDisable}
                          checked={paymentMethodCheckboxStatus[i]}
                          onChange={handleCheckboxChange(
                            i + beforePaymentMethodCount,
                            ele.id,
                          )}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle1"
                          className={classes.checkBoxPaddingLeft}
                        >
                          {ele.short_desc}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={4} sm={4} md={4} lg={4} xl={4} xxl={4}>
                <Typography variant="body2" className={classes.dataFieldLabel}>
                  {getLabel(documentType, 'label4')}
                </Typography>
                <Box className={classes.dataFieldsContainer}>
                  {paymentData?.map((ele, i) => (
                    <FormControlLabel
                      key={`Payment${i + 1}`}
                      className={classes.checkBoxMainClass}
                      control={
                        <Checkbox
                          disabled={checkFieldDisable}
                          checked={paymentCheckboxStatus[i]}
                          onChange={handleCheckboxChange(
                            i + beforePaymentCount,
                            ele.id,
                          )}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle1"
                          className={classes.checkBoxPaddingLeft}
                        >
                          {ele.short_desc}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
                <Typography variant="body2" className={classes.dataFieldLabel}>
                  {getLabel(documentType, 'label5')}
                </Typography>
                <Box className={classes.dataFieldsContainer}>
                  {customerData?.map((ele, i) => (
                    <FormControlLabel
                      key={`Product${i + 1}`}
                      className={classes.checkBoxMainClass}
                      control={
                        <Checkbox
                          disabled={checkFieldDisable}
                          checked={customerCheckboxStatus[i]}
                          onChange={handleCheckboxChange(
                            i + beforeCustomerCount,
                            ele.id,
                          )}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle1"
                          className={classes.checkBoxPaddingLeft}
                        >
                          {ele.short_desc}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={4} sm={4} md={4} lg={4} xl={4} xxl={4}>
                {productData && productData.length > 0 && (
                  <Typography
                    variant="body2"
                    className={classes.dataFieldLabel}
                  >
                    Product
                  </Typography>
                )}
                <Box className={classes.dataFieldsContainer}>
                  {productData &&
                    productData.length > 0 &&
                    productData?.map((ele, i) => (
                      <FormControlLabel
                        key={`Product${i + 1}`}
                        className={classes.checkBoxMainClass}
                        control={
                          <Checkbox
                            disabled={checkFieldDisable}
                            checked={productCheckboxStatus[i]}
                            onChange={handleCheckboxChange(
                              i + beforeProductCount,
                              ele.id,
                            )}
                          />
                        }
                        label={
                          <Typography
                            variant="subtitle1"
                            className={classes.checkBoxPaddingLeft}
                          >
                            {ele.short_desc}
                          </Typography>
                        }
                      />
                    ))}
                </Box>
                {bankDetails && bankDetails.length > 0 && (
                  <Typography
                    variant="body2"
                    className={classes.dataFieldLabel}
                  >
                    Bank Details
                  </Typography>
                )}
                <Box className={classes.dataFieldsContainer}>
                  {bankDetails &&
                    bankDetails.length > 0 &&
                    bankDetails?.map((ele, i) => (
                      <FormControlLabel
                        key={`Bank Details${i + 1}`}
                        className={classes.checkBoxMainClass}
                        control={
                          <Checkbox
                            disabled={checkFieldDisable}
                            checked={bankDetailsCheckboxStatus[i]}
                            onChange={handleCheckboxChange(
                              i + beforeBankDetailsCount,
                              ele.id,
                            )}
                          />
                        }
                        label={
                          <Typography
                            variant="subtitle1"
                            className={classes.checkBoxPaddingLeft}
                          >
                            {ele.short_desc}
                          </Typography>
                        }
                      />
                    ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Root>
  );
};

DataFields.propTypes = {
  documentType: PropTypes.string.isRequired,
};

export default DataFields;
